import { useCallback, useMemo, useReducer } from "react";

import { BackgroundActionData, TABLE_NAMES } from "shared";

import {
    REMOVE_ALERT,
    SET_ALERT,
    SET_BACKGROUND_ACTION_ID
} from "./alertActions";
import AlertContext from "./alertContext";
import AlertReducer from "./alertReducer";
import { AlertStateProps, SetAlertOptions } from "./types";

const AlertState = ({ children }: AlertStateProps) => {
    const initialState = {
        status: null,
        title: null,
        description: null,
        primaryBtn: null,
        secondaryBtn: null,
        hasCloseBtn: true,
        hasAutoHide: true,
        backgroundActionId: null,
        backgroundActionTableName: null,
        backgroundActionSuccessCallback: null,
        backgroundActionFailureCallback: null
    };

    const [state, dispatch] = useReducer(AlertReducer, initialState);

    const setAlert = (parameters: SetAlertOptions) => {
        const {
            status,
            title,
            description,
            primaryBtn,
            secondaryBtn,
            hasCloseBtn = true,
            hasAutoHide = true
        } = parameters;

        dispatch({
            type: SET_ALERT,
            payload: {
                status,
                title,
                description: description ?? null,
                primaryBtn: primaryBtn ?? null,
                secondaryBtn: secondaryBtn ?? null,
                hasCloseBtn,
                hasAutoHide
            }
        });
    };

    const removeAlert = () =>
        dispatch({
            type: REMOVE_ALERT
        });

    const removeAlertForBGAction = useCallback(() => {
        const { hasCloseBtn, hasAutoHide } = state;
        !hasCloseBtn && !hasAutoHide && removeAlert();
    }, [state]);

    const setBackgroundActionId = (
        responseData: BackgroundActionData,
        tableName: TABLE_NAMES,
        successCallbackFn: () => void,
        failureCallbackFn: () => void
    ) =>
        dispatch({
            type: SET_BACKGROUND_ACTION_ID,
            payload: {
                data: responseData,
                tableName,
                successCallbackFn,
                failureCallbackFn
            }
        });

    const value = useMemo(
        () => ({
            status: state.status,
            title: state.title,
            description: state.description,
            primaryBtn: state.primaryBtn,
            secondaryBtn: state.secondaryBtn,
            hasCloseBtn: state.hasCloseBtn,
            hasAutoHide: state.hasAutoHide,
            backgroundActionId: state.backgroundActionId,
            backgroundActionTableName: state.backgroundActionTableName,
            backgroundActionSuccessCallback:
                state.backgroundActionSuccessCallback,
            backgroundActionFailureCallback:
                state.backgroundActionFailureCallback,
            setAlert,
            setBackgroundActionId,
            removeAlert,
            removeAlertForBGAction
        }),
        [
            state.status,
            state.title,
            state.description,
            state.primaryBtn,
            state.secondaryBtn,
            state.hasCloseBtn,
            state.hasAutoHide,
            state.backgroundActionTableName,
            state.backgroundActionId,
            state.backgroundActionSuccessCallback,
            state.backgroundActionFailureCallback,
            removeAlertForBGAction
        ]
    );

    return <AlertContext value={value}>{children}</AlertContext>;
};

export default AlertState;
